import React, { useEffect, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2, Edit, Mail, Trash2, X } from 'lucide-react';
import { Tooltip } from '../../components/ui/Tooltip';
import { TrainingEmailModal } from '../../components/training/TrainingEmailModal';
import { usePractices } from '../../hooks/usePractices';
import { recipientGroups, sendPracticeEmail } from '../../utils/practiceEmail';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { PermissionGate } from '../../components/PermissionGate';
import { CourseInfoCard } from '../../components/training/course/CourseInfoCard';
import { CourseTeachersCard } from '../../components/training/course/CourseTeachersCard';
import { CoursePracticesCard } from '../../components/training/course/CoursePracticesCard';
import { AssignPracticesModal } from '../../components/training/course/AssignPracticesModal';
import { linkedPractices } from '../../utils/practices';
import { useTrainingBilling } from '../../hooks/useTrainingBilling';
import { courseEconomics } from '../../utils/courseEconomics';
import { formatEuro } from '../../utils/currency';
import { CourseKpi, CourseKpiCards } from '../../components/training/course/CourseKpiCards';
import { OnDemandParticipantsCard } from '../../components/training/course/OnDemandParticipantsCard';
import { CourseCostSummary } from '../../components/training/course/CourseCostSummary';
import { TeacherDetailModal } from '../../components/training/TeacherDetailModal';
import { CourseDeleteModal } from '../../components/training/CourseDeleteModal';
import { useTrainingCourses } from '../../hooks/useTrainingCourses';
import { trainingCoursesStore } from '../../services/trainingCoursesStore';
import { courseStatusLabels } from '../../data/trainingCourseOptions';
import { formatCourseDate, getTrainedCourses, getTrainingProduct } from '../../utils/trainingCourses';
type SavedState = {
  saved?: 'created' | 'updated';
} | null;
export function CourseDashboardPage() {
  const {
    courseId
  } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const courses = useTrainingCourses();
  const course = courses.find(c => c.id === courseId);
  const [banner, setBanner] = useState((location.state as SavedState)?.saved);
  const [teacherId, setTeacherId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEmailing, setIsEmailing] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);
  const {
    practices
  } = usePractices();
  const billing = useTrainingBilling();
  useEffect(() => {
    setBanner((location.state as SavedState)?.saved);
  }, [location.key]);
  useEffect(() => {
    if (!banner) return;
    const timer = setTimeout(() => setBanner(undefined), 4000);
    return () => clearTimeout(timer);
  }, [banner]);
  if (!course) return <Navigate to="/training/corsi" replace data-id="element-6161" data-mp="3p6vtpemss2tu" />;

  // Practices assigned to this course: their participants are the course users
  const enrolled = linkedPractices(practices, course.courseCode);
  const category = getTrainingProduct(course.productCode)?.category;
  const listHref = `/training/corsi?tab=${course.status}`;
  const period = course.startDate || course.endDate ? `${formatCourseDate(course.startDate) || '—'} → ${formatCourseDate(course.endDate) || '—'}` : '';
  const isOnDemand = course.status === 'ondemand';
  const economics = courseEconomics(course, practices, billing);
  const enrollments = course.enrollments || [];
  const kpis: CourseKpi[] = isOnDemand ? [{
    label: 'Iscritti totali',
    value: String(enrollments.length)
  }, {
    label: 'Credenziali da inviare',
    value: String(enrollments.filter(e => !e.credentialsSentAt).length),
    tone: enrollments.some(e => !e.credentialsSentAt) ? 'attention' : 'default'
  }, {
    label: 'Corso completato',
    value: String(enrollments.filter(e => e.completed).length),
    detail: `su ${enrollments.length} iscritti`
  }, {
    label: 'Attestato da inviare',
    value: String(enrollments.filter(e => !e.certificateSentAt).length),
    tone: enrollments.some(e => e.completed && !e.certificateSentAt) ? 'attention' : 'default'
  }] : [{
    label: 'Ricavi',
    value: formatEuro(economics.revenue),
    detail: 'Pratiche assegnate, senza IVA'
  }, {
    label: 'Costi',
    value: formatEuro(economics.costs),
    detail: 'Totale riepilogo costi'
  }, {
    label: 'Margine',
    value: formatEuro(economics.margin),
    detail: 'Ricavi − costi',
    tone: economics.margin < 0 ? 'negative' : 'positive'
  }, {
    label: 'Fatturato',
    value: formatEuro(economics.invoiced),
    detail: 'Fatture emesse'
  }];
  return <Layout data-id="element-6162" data-mp="31gi2wjj8itui">
      <Link to={listHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 hover:text-seform-navy mb-4" data-id="element-6163" data-mp="31fyhutyafpc9">
        <ArrowLeft className="h-4 w-4" data-id="element-6164" data-mp="3b6t4w0jcysyr" /> Corsi
      </Link>

      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 mb-6" data-id="element-6165" data-mp="369b632z21u4h">
        <div className="min-w-0" data-id="element-6166" data-mp="2ijgv6qyaqfmv">
          <p className="font-mono text-sm text-slate-500" data-id="element-6167" data-mp="2iki5gt7hmy3j">{course.courseCode}</p>
          <h1 className="text-3xl font-bold text-seform-navy mt-1" data-id="element-6168" data-mp="2dkvrghju5dto">
            {course.name || 'Corso senza nome'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-3" data-id="element-6169" data-mp="2sied3b17vaww">
            <Badge variant="info" data-id="element-6170" data-mp="2ni0iauomlxgt">{courseStatusLabels[course.status]}</Badge>
            <Badge variant="neutral" data-id="element-6171" data-mp="7rdx75ygcvgs">{course.characteristic}</Badge>
            {category && <Badge variant="primary" data-id="element-6172" data-mp="7tic0ixw3evu">{category}</Badge>}
            {period && <span className="text-sm text-slate-500 ml-1 tabular-nums" data-id="element-6173" data-mp="2rswapzq2d1f">
                {period}
              </span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-3 flex-shrink-0" data-id="element-6174" data-mp="eu2k50arx1ia">
          <Tooltip content={enrolled.length > 0 ? `Invia a tutti i ${enrolled.length} clienti iscritti` : 'Nessun cliente iscritto a questo corso'} data-id="element-6175" data-mp="ewf23dgj7bvc">
            <Button variant="flat" color="secondary" startContent={<Mail className="h-4 w-4" data-id="element-6177" data-mp="3s8yxicaxlcw8" />} disabled={enrolled.length === 0} onClick={() => setIsEmailing(true)} data-id="element-6176" data-mp="3naxe1q9lfbk8">
              Email ai partecipanti
              {enrolled.length > 0 && ` (${enrolled.length})`}
            </Button>
          </Tooltip>
          <PermissionGate module="training" action="delete" data-id="element-6178" data-mp="34k5wi1fgceur">
            <Button variant="flat" color="danger" startContent={<Trash2 className="h-4 w-4" data-id="element-6180" data-mp="39h66gnffa1r5" />} onClick={() => setIsDeleting(true)} data-id="element-6179" data-mp="34jnwctpo5ghk">
              Elimina
            </Button>
          </PermissionGate>
          <PermissionGate module="training" action="edit" data-id="element-6181" data-mp="1mnm42wc1z7p0">
            <Button startContent={<Edit className="h-4 w-4" data-id="element-6183" data-mp="1hm51mwdx7d6b" />} onClick={() => navigate(`/training/corsi/${course.id}/modifica`)} data-id="element-6182" data-mp="1hmy25fmad6g0">
              Modifica
            </Button>
          </PermissionGate>
        </div>
      </div>

      <AnimatePresence data-id="element-6184" data-mp="17r4tw8flnz41">
        {banner && <motion.div initial={{
        opacity: 0,
        y: -6
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} role="status" className="mb-6 flex items-center gap-3 rounded-xl bg-green-50 border border-green-200 px-4 py-3" data-id="element-6185" data-mp="12xs9kahyeeh3">
            <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" data-id="element-6186" data-mp="p0dcqkpdf4f3" />
            <p className="flex-1 text-sm font-medium text-green-700" data-id="element-6187" data-mp="jzzc3nq3mwr3">
              {banner === 'created' ? 'Corso creato con successo' : 'Corso modificato con successo'}
            </p>
            <button type="button" aria-label="Chiudi messaggio" onClick={() => setBanner(undefined)} className="text-green-600 hover:text-green-800" data-id="element-6188" data-mp="2ct4bgsy2hcxe">
              <X className="h-4 w-4" data-id="element-6189" data-mp="1p5wa2m3reo0n" />
            </button>
          </motion.div>}
      </AnimatePresence>

      <CourseKpiCards items={kpis} data-id="element-6190" data-mp="1tww7dsgsj83o" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-id="element-6191" data-mp="1txxghre8xkir">
        <div className="lg:col-span-2 space-y-6" data-id="element-6192" data-mp="1g60tg1rfq0t8">
          <CourseInfoCard course={course} courses={courses} data-id="element-6193" data-mp="1g34uca3up637" />
          {isOnDemand ? <OnDemandParticipantsCard course={course} data-id="element-6194" data-mp="1l1yakaaivb72" /> : <>
              <CoursePracticesCard practices={enrolled} onAssign={() => setIsAssigning(true)} data-id="element-6195" data-mp="167jyktpdl21w" />
              <CourseTeachersCard teachers={course.teachers || []} onOpenTeacher={setTeacherId} data-id="element-6196" data-mp="rjtbjnwx89ww" />
            </>}
        </div>
        <aside className="lg:sticky lg:top-6" data-id="element-6197" data-mp="nku8y9ynwp8m">
          <CourseCostSummary course={course} data-id="element-6198" data-mp="11jal4gfan7y5" />
        </aside>
      </div>

      <TrainingEmailModal isOpen={isEmailing} title="Email ai partecipanti del corso" context={`${course.courseCode} · ${course.name || 'Corso senza nome'}`} groups={recipientGroups(enrolled)} onClose={() => setIsEmailing(false)} onSend={body => sendPracticeEmail(enrolled, body)} data-id="element-6199" data-mp="wimu3ur2er3x" />
      <AssignPracticesModal isOpen={isAssigning} course={course} practices={practices} onClose={() => setIsAssigning(false)} data-id="element-6200" data-mp="1k88rccnvmw9a" />
      <TeacherDetailModal teacherId={teacherId} onClose={() => setTeacherId(null)} data-id="element-6201" data-mp="1u6eo6futtc7a" />
      <CourseDeleteModal course={isDeleting ? course : null} trainedCount={getTrainedCourses(courses, course.id).length} onClose={() => setIsDeleting(false)} onConfirm={c => {
      navigate(listHref);
      trainingCoursesStore.remove(c.id);
    }} data-id="element-6202" data-mp="1p8mqf57i2vv6" />
    </Layout>;
}